import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

// A scrolling trace of how far off pitch the note is: a bubble with the cents
// rides along the top and leaves a trail that streams down the grid, so you
// can watch the line walk in to the centre as you tune.

// Cents either side of the centre line; further out is pinned to the edge.
const RANGE = 50
// How fast the trail streams down (px per second).
const SCROLL_SPEED = 50
// Where the bubble rides, from the top of the trace (px).
const HEAD_Y = 56
// Space kept at each side so the bubble isn't clipped (px). Matches the
// in-tune band in tuner.scss.
const EDGE = 26
// Smoothing for the bubble's movement (ms); the trail follows the bubble.
const EASE_MS = 70
// Grid square size (px), as drawn by the trace background in tuner.scss.
const GRID_SIZE = 24

export default function TunerTrace({ cents, stale, inTuneCents, hint, waitingText }) {
    const containerRef = useRef(null)
    const canvasRef = useRef(null)
    const headRef = useRef(null)

    // The animation loop reads the latest reading without restarting.
    const latest = useRef({ cents, stale })
    latest.current = { cents, stale }

    useEffect(() => {
        const container = containerRef.current
        const canvas = canvasRef.current
        const head = headRef.current
        const context = canvas.getContext('2d')

        let width = 0
        let height = 0
        const resize = () => {
            const rect = container.getBoundingClientRect()
            const ratio = window.devicePixelRatio || 1
            width = rect.width
            height = rect.height
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
        }
        resize()
        window.addEventListener('resize', resize)

        const styles = getComputedStyle(container)
        const goodColour = styles.getPropertyValue('--tuner-good').trim() || '#3aa76d'
        const offColour = styles.getPropertyValue('--tuner-off').trim() || '#ec4365'
        const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const xFor = (c) => {
            const clamped = Math.max(-RANGE, Math.min(RANGE, c))
            return EDGE + ((clamped + RANGE) / (2 * RANGE)) * (width - 2 * EDGE)
        }
        const yFor = (age) => HEAD_Y + (age * SCROLL_SPEED) / 1000

        // { t, cents } per frame; cents is null where there was no note.
        const trail = []
        let shown = null
        let last = performance.now()
        let frame

        const draw = (now) => {
            frame = requestAnimationFrame(draw)
            const dt = Math.min(now - last, 100)
            last = now

            const reading = latest.current
            if (reading.cents === null) {
                shown = null
            } else if (shown === null) {
                shown = reading.cents
            } else {
                shown += (reading.cents - shown) * (1 - Math.exp(-dt / EASE_MS))
            }

            trail.push({ t: now, cents: shown === null || reading.stale ? null : shown })
            const maxAge = ((height - HEAD_Y) / SCROLL_SPEED) * 1000
            while (trail.length > 0 && now - trail[0].t > maxAge) {
                trail.shift()
            }

            context.clearRect(0, 0, width, height)
            context.lineWidth = 2.5
            context.lineJoin = 'round'
            context.lineCap = 'round'
            // One stroke per run of same-coloured, unbroken segments.
            let runColour = null
            for (let i = 1; i < trail.length; i++) {
                const a = trail[i - 1]
                const b = trail[i]
                if (a.cents === null || b.cents === null) {
                    if (runColour) {
                        context.stroke()
                        runColour = null
                    }
                    continue
                }
                const colour = Math.abs(b.cents) <= inTuneCents ? goodColour : offColour
                if (colour !== runColour) {
                    if (runColour) {
                        context.stroke()
                    }
                    context.strokeStyle = colour
                    context.beginPath()
                    context.moveTo(xFor(a.cents), yFor(now - a.t))
                    runColour = colour
                }
                context.lineTo(xFor(b.cents), yFor(now - b.t))
            }
            if (runColour) {
                context.stroke()
            }

            if (shown !== null) {
                const x = xFor(shown)
                head.style.transform = `translateX(${x}px)`
                // Keep the hint on whichever side has room.
                head.classList.toggle('flip', x > width * 0.6)
            }

            const streaming = trail.some(point => point.cents !== null)
            if (streaming && !reduceMotion) {
                container.style.backgroundPosition = `0 ${((now * SCROLL_SPEED) / 1000) % GRID_SIZE}px`
            }
        }
        frame = requestAnimationFrame(draw)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('resize', resize)
        }
    }, [inTuneCents])

    const waiting = cents === null
    const rounded = waiting ? 0 : Math.round(cents)
    const label = waiting ? '' : `${rounded > 0 ? '+' : ''}${rounded === 0 ? 0 : rounded}`

    return (
        <div ref={containerRef} className={classNames('tuner-trace', { 'tuner-trace--waiting': waiting })}>
            <canvas ref={canvasRef} className="tuner-trace-canvas" aria-hidden="true" />
            <span className="tuner-trace-flat" aria-hidden="true">♭</span>
            <span className="tuner-trace-sharp" aria-hidden="true">♯</span>
            <div ref={headRef} className="tuner-trace-head">
                <div className="tuner-trace-bubble">{label}</div>
                <div className="tuner-trace-hint">{hint}</div>
            </div>
            {waiting &&
                <div className="tuner-trace-waiting">{waitingText}</div>
            }
        </div>
    )
}
